import { useState, useEffect } from 'react'
import { animated, useSpring } from '@react-spring/web'
import './Toast.scss'

type ToastVariation = 'error'
type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

type ToastProps = {
  message: string
  show: boolean
  duration?: number
  position?: ToastPosition
  variant?: ToastVariation
}

// To future devs, if you ever need to implement a queue with multiple toast
// messages, see here for an example using React Spring: https://codesandbox.io/s/v1i1t?file=/src/App.tsx
/**
 * **An accessible, mobile-friendly toast component**.
 * @param message the message to display in the toast.
 * @param show if true, show the toast, else don't don't it
 * @param duration a number in ms to fade the toast. Defaults to 10000ms.
 * @param position the position the toast is rendered on the parent div.
 * Top-left, top-right, bottom-left and bottom-right are valid values. Defaults to bottom-right.
 * @param variant the type of toast to display, either standard, or error
 * @author Phillip Berger
 */
export const Toast: React.FC<ToastProps> = ({
  message,
  show = false,
  duration = 10000,
  position = 'bottom-right',
  variant = 'standard',
}) => {
  const variantClassName = variant.toLowerCase()
  const positionClassName = position.toLowerCase()

  const [shouldShowToast, setShouldShowToast] = useState(show)

  useEffect(() => {
    if (show) {
      handleShowToast()
    }
  }, [show])

  const [styles, springApi] = useSpring(() => ({
    from: {
      opacity: 0,
    },
  }))

  const handleShowToast = () => {
    springApi.start({
      to: {
        opacity: 1,
      },
      onStart() {
        setShouldShowToast(true)
      },
    })
    setTimeout(() => {
      handleHideToast()
    }, duration)
  }

  const handleHideToast = () => {
    springApi.start({
      to: {
        opacity: 0,
      },
      onRest() {
        setShouldShowToast(false)
      },
    })
  }

  const handleClick = () => {
    handleHideToast()
  }

  return (
    <animated.div
      data-testid="Toast"
      style={styles}
      className={`CLToast CLToast--${variantClassName} CLToast--${positionClassName}
      ${shouldShowToast ? '' : 'CLToast--visuallyhidden'}`}
      aria-label="Toast Message"
      aria-hidden={!shouldShowToast}
    >
      <div data-testid="CLToast__message" className="CLToast__message">
        {message}
      </div>
      <button
        data-testid="CLToast__closeBtn"
        className="CLToast__close"
        aria-label="Close Toast Message"
        tabIndex={0}
        onClick={handleClick}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3007 0.710703C12.9107 0.320703 12.2807 0.320703 11.8907 0.710703L7.0007 5.5907L2.1107 0.700703C1.7207 0.310703 1.0907 0.310703 0.700703 0.700703C0.310703 1.0907 0.310703 1.7207 0.700703 2.1107L5.5907 7.0007L0.700703 11.8907C0.310703 12.2807 0.310703 12.9107 0.700703 13.3007C1.0907 13.6907 1.7207 13.6907 2.1107 13.3007L7.0007 8.4107L11.8907 13.3007C12.2807 13.6907 12.9107 13.6907 13.3007 13.3007C13.6907 12.9107 13.6907 12.2807 13.3007 11.8907L8.4107 7.0007L13.3007 2.1107C13.6807 1.7307 13.6807 1.0907 13.3007 0.710703Z"
            fill="white"
          />
        </svg>
      </button>
    </animated.div>
  )
}
